import { createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router-dom'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { CssBaseline, StyledEngineProvider } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { I18nextProvider } from 'react-i18next'
import i18n from './config/i18n'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import dayjsBusinessDays from 'dayjs-business-days2'
import objectSupport from 'dayjs/plugin/objectSupport'
import isBetween from 'dayjs/plugin/isBetween'
import duration from 'dayjs/plugin/duration'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import 'dayjs/locale/pl'
import 'dayjs/locale/en'
import { Notification } from './components/Notification/Notification'
import { HelmetProvider } from 'react-helmet-async'
import { ThemeProvider } from '@mui/material'
import { getCurrentMode, setMode } from './store/redux/reducers/darkModeSlice'
import { useSelector, useDispatch } from 'react-redux'
import { PaletteMode } from '@mui/material'
import { darkTheme, lightTheme } from './styles/theme-configuration'
import { useEffect, useMemo, useState } from 'react'
import routing from './components/Routing/Routing'
import { environment } from './enviroments/enviroment'
import { enUS, plPL } from '@mui/x-date-pickers/locales'

function App() {
  dayjs.locale(i18n.language)
  dayjs.extend(dayjsBusinessDays)
  dayjs.extend(objectSupport)
  dayjs.extend(utc)
  dayjs.extend(isBetween)
  dayjs.extend(duration)
  dayjs.extend(isSameOrAfter)

  const dispatch = useDispatch()
  const mode = useSelector(getCurrentMode)
  const [locale, setLocale] = useState(i18n.language)

  useEffect(() => {
    const updateLocale = () => {
      const newLocale = i18n.language
      dayjs.locale(newLocale)
      setLocale(newLocale)
    }
    i18n.on('languageChanged', updateLocale)

    updateLocale()

    return () => {
      i18n.off('languageChanged', updateLocale)
    }
  }, [])

  let savedMode = localStorage.getItem('darkMode')

  useEffect(() => {
    if (savedMode !== null) {
      dispatch(setMode(savedMode as PaletteMode))
    } else {
      localStorage.setItem('darkMode', 'light')
    }
  }, [dispatch, savedMode])

  const localeText = useMemo(() => {
    return locale === 'pl'
      ? plPL.components.MuiLocalizationProvider.defaultProps.localeText
      : enUS.components.MuiLocalizationProvider.defaultProps.localeText
  }, [locale])

  const router = createBrowserRouter(createRoutesFromElements(routing), { basename: environment.publicUrl })

  return (
    <StyledEngineProvider injectFirst>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale} localeText={localeText}>
        <I18nextProvider i18n={i18n}>
          <HelmetProvider>
            <ThemeProvider theme={mode === 'light' ? lightTheme : darkTheme}>
              <CssBaseline />
              <RouterProvider router={router} />
              <Notification />
            </ThemeProvider>
          </HelmetProvider>
        </I18nextProvider>
      </LocalizationProvider>
    </StyledEngineProvider>
  )
}

export default App
